import React, { useEffect, useState } from "react";
import ProofingNotesProps from "./ProofingNotes";
import moment, { Moment } from "moment";
import { daysOfWeek, sortAlphabetically } from '../../common/utils';
import DataTable from "../dataTable";
import WeekItem from "../../api/models/timesheetWeekItem";
import TimesheetWeekEntry from '../../api/models/TimesheetWeekEntry';
import TimesheetWeekTask from "../../api/models/TimesheetWeekTask";
import { getTaskTypes } from '../../api/services/taskTypeService';
import { useAsync } from "react-async";
import TaskType from "../../api/models/tasktype";
import EditEntry from "../logTimeModal/types/editEntry";

const fetchTaskTypes = async () => getTaskTypes();

const ProofingNotes = React.memo(({
  approvalHandler,
  className = '',
  header = 'Notes',
  modalHandler,
  showOnlyRejected = false,
  week
}: ProofingNotesProps) => {
  //Days array. Contains the week items that have notes with the day included.
  const [days, setDays] = useState<WeekItem[]>([]);
  const [entries, setEntries] = useState<TimesheetWeekEntry[]>([]);
  const [isSelect, setIsSelect] = useState<boolean>(false);
  const [selected, setSelected] = useState<TimesheetWeekEntry[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [taskTypeId, setTaskTypeId] = useState<number>(-1);

  useAsync({ promiseFn: fetchTaskTypes, onResolve: (r) => setTaskTypes(sortAlphabetically(r.data.taskTypes ?? [], 'name')) });

  //DataTable Columns
  const columns = React.useMemo(
    () => [
      {
        Header: ' ',
        accessor: (d: any) => {
          const entry: TimesheetWeekEntry = d;

          return (
            <input
              id={`entry_`}
              className="datatable-checkbox"
              type='checkbox'
              value={entry.entryId}
              onClick={(e: any) => {
                setSelected(current => {
                  if (e.target.checked) {
                    entry.timesheetEntryFlags.map(flag => flag.timesheetWeekId = week.weekId)
                    return [...current, entry];
                  }

                  return [...current.filter(s => s.entryId !== entry.entryId)];
                });

                e.stopPropagation();
              }}
            />
          )
        },
        width: 10,
        disableSortBy: true
      },
      {
        Header: 'District',
        accessor: (d: any) => (d as TimesheetWeekEntry).parentWeekItem?.description,
        //width: '90%'
      },
      {
        Header: 'School',
        accessor: (d: any) => (d as TimesheetWeekEntry).parentWeekItem?.subTitle,
        //width: '10%'
      },
      {
        Header: 'Entry',
        accessor: (d: any) => (d as TimesheetWeekEntry).parentWeekItem?.title,
        //width: '10%'
      },
      {
        Header: 'Task',
        accessor: (d: any) => (d as TimesheetWeekEntry).parentTask?.taskTypeName
        //width: '10%'
      },
      {
        Header: 'Date',
        accessor: (d: any) => {
          const timesheetWeekEntry: TimesheetWeekEntry = d;
          return timesheetWeekEntry.dateOfEntry && moment(timesheetWeekEntry.dateOfEntry).isValid() ? moment(timesheetWeekEntry.dateOfEntry).format('MM/DD/YYYY') : 'N/A'
        },
      },
      {
        Header: 'Mins',
        accessor: (d: any) => {
          const timesheetWeekEntry: TimesheetWeekEntry = d;
          return timesheetWeekEntry.minutes
        },
      },
      {
        Header: 'Flag',
        accessor: (d: any) => {
          const entry: TimesheetWeekEntry = d;
          return entry.timesheetEntryFlags && entry.timesheetEntryFlags.length > 0 && entry.timesheetEntryFlags.filter(x => x.proofingRule && x.proofingRule.name && x.proofingRule.name !== '').length > 0 ? <ul className="list-disc">
            {entry.timesheetEntryFlags.filter(x => x.proofingRule && x.proofingRule.name && x.proofingRule.name !== '').map(flag => <li key={`flag-${entry.entryId}-${flag.proofingRule.proofingRuleId}`}>{flag.proofingRule.name}</li>)}
          </ul> : <>N/A</>;
        },
      },
      {
        Header: 'Notes',
        accessor: (d: any) => {
          const entry: TimesheetWeekEntry = d;
          return entry.notes
        }
      },
      {
        Header: 'Action',
        accessor: (d: any) => d.timesheetEntryFlags.length === 0 ? <>N/A</> : <span className="underline cursor-pointer hover:no-underline" onClick={() => {
          const entry: TimesheetWeekEntry = d;
          var ed: EditEntry = {
            districtId: entry.parentWeekItem?.districtId ?? -1,
            schoolId: entry.parentTask?.schoolId ?? -1,
            subTitle: entry.parentWeekItem?.subTitle ?? '',
            task: entry.parentTask ?? {} as TimesheetWeekTask,
            title: entry.parentWeekItem?.title ?? '',
            entry: entry,
            students: []
          }

          modalHandler(ed, true);
        }}>Reject W/Feedback</span>,
      },
      {
        Header: 'Rejection Feedback',
        accessor: (d: any) => {
          const entry: TimesheetWeekEntry = d;
          return entry.timesheetEntryFlags && entry.timesheetEntryFlags.length > 0 && entry.timesheetEntryFlags.filter(x => x.feedback && x.feedback !== '').length > 0 ? <ul className="list-disc">
            {entry.timesheetEntryFlags.filter(x => x.feedback && x.feedback !== '').map(flag => <li key={`feedback-${entry.entryId}-${flag.proofingRule.proofingRuleId}`}>{flag.feedback}</li>)}
          </ul> : <>N/A</>;
        },
      },
      { //Hidden field so filter bar can search for flags
        id: 'flags',
        accessor: (d: any) => {
          const entry: TimesheetWeekEntry = d;
          return entry.timesheetEntryFlags.map(x => x.proofingRule.name).join(', ')
        },
        className: 'hidden'
      },
    ],
    []
  );

  //Any time the week changes, update our days array
  useEffect(() => {
    const daysItems: WeekItem[] = [];
    const d: Moment[] = daysOfWeek(week.firstDayOfWeek);
    const tmpEntries: TimesheetWeekEntry[] = [];

    d.map((day) => {
      const weekItems: WeekItem[] = [];

      if (week.items.some(items =>
        items.tasks.some(task =>
          task.entries.some(entry =>
            moment(entry.dateOfEntry).isSame(day) && entry.notes !== "" && (showOnlyRejected ? entry.timesheetEntryFlags.length > 0 : true)
          )
        )
      )) {
        week.items.filter(items =>
          items.tasks.filter(task =>
            task.entries.map(entry => {
              if (moment(entry.dateOfEntry).isSame(day) && entry.notes !== "" && (showOnlyRejected ? entry.timesheetEntryFlags.length > 0 : true)
              ) {
                entry.parentWeekItem = items;
                entry.parentTask = task;
                tmpEntries.push(entry);
              }

              return moment(entry.dateOfEntry).isSame(day) && entry.notes !== "" && (showOnlyRejected ? entry.timesheetEntryFlags.length > 0 : true)
            })
          )
        )
          .map((item) => {
            item.day = day;
            weekItems.push(item);
            daysItems.push(item);
          });
      }
    });

    setEntries([...tmpEntries]);
    setDays(daysItems);
  }, [week]);

  /**
   * This function selects or de-selects all checkboxes in a datatable
   *
   * @return {void} This function does not return anything.
   */
  const selectAll = (): void => {
    //Get all checkboxes
    const checkboxes = [...document.querySelectorAll('.datatable-checkbox')];

    //select or de-select based on the previous state
    checkboxes.map(box => setTimeout(() => (box as HTMLInputElement).checked = !isSelect, 0));

    const newSelected: TimesheetWeekEntry[] = [];

    //Loop through the days and gets all the weeks
    if (!isSelect) {
      entries.map(day => {
        day.timesheetEntryFlags.map(flag => flag.timesheetWeekId = week.weekId)
        newSelected.push(day)
      });
    }

    setSelected([...newSelected]);
    setIsSelect(current => !current);
  }

  const unSelectAll = (): void => {
    //Get all checkboxes
    const checkboxes = [...document.querySelectorAll('.datatable-checkbox')];

    //select or de-select based on the previous state
    checkboxes.map(box => setTimeout(() => (box as HTMLInputElement).checked = false, 0));
    setSelected([]);
    setIsSelect(false);
  }

  useEffect(() => {
    //if (taskTypeId < 1) return;
    setEntries(current => [...[...current]]);
  }, [taskTypeId]);

  /**
   * JSX element for below the filter element on a datatable
   */
  const approvalBar = <div className="pb-0.5 mt-3 mb-0.5 approval-bar">
    <div className="mb-4 ml-2">
      <span className="font-semibold">Task Type</span>
      <select
        className="w-1/4 ml-2"
        value={taskTypeId}
        onChange={(e) => {
          //if (Number(e.target.value) < 1) return;
          setTaskTypeId(e.target.value ? Number(e.target.value) : -1);
        }}
      >
        <option key={`task-type-filter-option-${-1}`} value={-1}>Show All</option>
        {taskTypes.map((taskType) => <option key={`task-type-filter-option-${taskType.taskTypeId}`} value={taskType.taskTypeId}>{taskType.name}</option>)}
      </select>
    </div>
    <button
      onClick={selectAll}
      className='inline-flex justify-center px-4 py-2 text-base text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-ptsblue-normal hover:bg-ptsblue-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm'
    >{isSelect ? 'Uns' : 'S'}elect All</button>
    <button
      type="button"
      disabled={selected.length === 0}
      onClick={() => {
        approvalHandler(selected);
        unSelectAll();
      }}
      className='inline-flex justify-center px-4 py-2 text-base text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-ptsgreen-normal hover:bg-ptsgreen-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm'
    >Approve</button>
  </div>;

  return <div className={`proofing-notes-table ${className}`}>
    <h2 className='ml-1 font-bold'>{header}</h2>
    <DataTable
      columns={columns}
      data={entries.filter(x => taskTypeId > 0 ? x.parentTask?.taskTypeId === taskTypeId : true)}
      showPagination={true}
      postFilter={approvalBar}
      resetOnDataChange={false}
    />
  </div>
});
export default ProofingNotes;